import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { catchError, map, switchMap, tap } from 'rxjs';
import { StateService } from '../shared/state.service';
import { SuccessToastService } from '../shared/success-toast.service';

interface StatusLookupEntry {
    /** FHIR value set source or label */
    source: string;
    codes: string[];
}

const RESOURCE_STATUS_LOOKUP: Record<string, StatusLookupEntry> = {
    Location: {
        source: 'FHIR Location status (https://www.hl7.org/fhir/location-definitions.html)',
        codes: ['active', 'suspended', 'inactive']
    },
    MedicationRequest: {
        source: 'MedicationRequest.status (https://www.hl7.org/fhir/medicationrequest-definitions.html#MedicationRequest.status)',
        codes: ['active', 'completed', 'entered-in-error', 'on-hold', 'revoked', 'unknown', 'draft']
    },
    ServiceRequest: {
        source: 'ServiceRequest.status (https://www.hl7.org/fhir/servicerequest-definitions.html#ServiceRequest.status)',
        codes: ['draft', 'active', 'on-hold', 'revoked', 'completed', 'entered-in-error', 'unknown']
    },
    Specimen: {
        source: 'Specimen.status (https://www.hl7.org/fhir/specimen.html#specimen-status)',
        codes: ['available', 'unavailable', 'unsatisfactory', 'entered-in-error', 'unknown', 'ready']
    },
    MedicationDispense: {
        source: 'MedicationDispense.status (https://www.hl7.org/fhir/medicationdispense-definitions.html#MedicationDispense.status)',
        codes: ['preparation', 'in-progress', 'cancelled', 'on-hold', 'completed', 'entered-in-error', 'stopped', 'unknown']
    },
    Observation: {
        source: 'Observation.status (https://www.hl7.org/fhir/observation.html#status)',
        codes: ['registered', 'preliminary', 'final', 'amended', 'corrected', 'cancelled', 'entered-in-error', 'unknown']
    },
    Condition: {
        source: 'Condition.clinicalStatus (https://www.hl7.org/fhir/condition-definitions.html#Condition.clinicalStatus)',
        codes: ['active', 'recurrence', 'relapse', 'inactive', 'remission', 'resolved']
    },
    MedicationAdministration: {
        source: 'MedicationAdministration.status (https://www.hl7.org/fhir/medicationadministration-definitions.html#MedicationAdministration.status)',
        codes: ['in-progress', 'on-hold', 'completed', 'entered-in-error', 'stopped', 'unknown']
    },
    Task: {
        source: 'Task.status (https://www.hl7.org/fhir/task-definitions.html#Task.status)',
        codes: ['draft', 'requested', 'received', 'accepted', 'rejected', 'ready', 'cancelled', 'in-progress', 'on-hold', 'failed', 'completed', 'entered-in-error']
    },
    Encounter: {
        source: 'Encounter.status (https://www.hl7.org/fhir/encounter-definitions.html#Encounter.status)',
        codes: ['planned', 'arrived', 'triaged', 'in-progress', 'onleave', 'finished', 'cancelled']
    },
    AllergyIntolerance_clinicalStatus: {
        source: 'AllergyIntolerance.clinicalStatus',
        codes: ['active', 'inactive', 'resolved']
    },
    AllergyIntolerance_verificationStatus: {
        source: 'AllergyIntolerance.verificationStatus',
        codes: ['unconfirmed', 'presumed', 'confirmed', 'refuted', 'entered-in-error']
    }
};

@Injectable({ providedIn: 'root' })
export class ResourceStatusChangesService {
    private http = inject(HttpClient);
    private backend = inject(backendEndPointToken);
    private stateService = inject(StateService);
    private successToast = inject(SuccessToastService);

    getStatusLookup(resourceType: string): StatusLookupEntry | undefined {
        return RESOURCE_STATUS_LOOKUP[resourceType];
    }

    getStatusCodes(resourceType: string): string[] {
        return this.getStatusLookup(resourceType)?.codes ?? [];
    }

    changeResourceStatus(
        resourceType: string,
        resourceId: string,
        newStatus: string,
        persist: 'auto' | 'patient' | 'org' = 'auto',
        statusField: string = 'status'
    ) {
        const endpoint = `${this.backend}/${resourceType}/${resourceId}`;

        // PUT (full resource) update flow:
        // 1) GET current representation
        // 2) mutate status
        // 3) PUT back with Prefer: return=representation
        return this.http.get<any>(endpoint, {
            headers: {
                'Accept': 'application/fhir+json'
            }
        }).pipe(
            switchMap((resource: any) => {
                if (!resource || resource.resourceType === 'OperationOutcome') {
                    throw new Error(`Failed to fetch ${resourceType}/${resourceId} before status update.`);
                }
                
                if (statusField === 'clinicalStatus') {
                    resource.clinicalStatus = { coding: [{ code: newStatus }] };
                } else if (statusField === 'verificationStatus') {
                    resource.verificationStatus = { coding: [{ code: newStatus }] };
                } else {
                    // For Condition, if someone uses 'status' default, fallback to clinicalStatus structure if they actually meant it? 
                    // To be safe and compatible with older code:
                    if (resourceType === 'Condition') {
                        resource.clinicalStatus = { coding: [{ code: newStatus }] };
                    } else {
                        resource.status = newStatus;
                    }
                }
                
                return this.http.put<any>(endpoint, resource, {
                    headers: {
                        'Content-Type': 'application/fhir+json',
                        'Accept': 'application/fhir+json',
                        'Prefer': 'return=representation'
                    }
                });
            }),
            tap((resp: any) => {
                if (!resp || resp.resourceType === 'OperationOutcome') return;
                if (persist === 'org') {
                    this.stateService.persistOrgWideResource(resp, 'saved');
                } else if (persist === 'patient') {
                    this.stateService.persistPatientResource(resp, 'saved');
                } else {
                    this.stateService.persistLocalResource(resp, 'saved');
                }

                this.successToast.success(`${resourceType} status updated to "${newStatus}".`);
            }),
            map(response => ({ resourceType, resourceId, status: newStatus, response })),
            catchError((error) => {
                console.error(`Failed to change status for ${resourceType}/${resourceId}`, error);
                throw error;
            })
        );
    }
}
